import type { UserInfo } from '@/types/UserData';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import { useModalStore } from '@/stores/apostatudo/modal';
import AnnouncementModal from '@/components/apostatudo/organisms/AnnouncementModal.vue';

const userLoginData = ref<ILoginSubmitData>();
const twoFactorType = ref<TwoFactorOptions['type']>();

interface TwoFactorOptions {
  loginData?: ILoginSubmitData;
  type?: UserInfo['two_factor_type'];
}

export const useAuth = () => {
  const { t } = useTexts();
  const { authConfigsFromApi, isMpMaintenanceModeEnabled } =
    useFeatureFlagsNew();

  const {
    openModal,
    closeModal,
    openToaster,
    setAuthInitialTab,
    setAuthComplianceMode,
  } = useLayoutStore();

  const { currentRouteName } = useRoutes();

  // Functions AuthConfigs

  function getStringDefaultValue(options: {
    key: keyof AuthConfigs;
    defaultValue?:
      | AuthFlowVersion
      | AuthLayoutVersion
      | RegisterTypeVersion
      | string;
  }) {
    const { key, defaultValue } = options;

    if (
      authConfigsFromApi.value[key] === null ||
      authConfigsFromApi.value[key] === undefined
    ) {
      return defaultValue;
    }

    return authConfigsFromApi.value[key];
  }

  function getBooleanDefaultValue(options: {
    key: keyof AuthConfigs;
    defaultValue: boolean;
  }): boolean {
    const { key, defaultValue } = options;

    if (
      authConfigsFromApi.value[key] === null ||
      authConfigsFromApi.value[key] === undefined
    ) {
      return defaultValue;
    }

    return !!authConfigsFromApi.value[key];
  }

  // Values w/ default

  const authFlowVersion = computed<AuthFlowVersion>(() => {
    return getStringDefaultValue({
      key: 'auth_flow_version',
      defaultValue: 'with_modal',
    }) as AuthFlowVersion;
  });

  const authLayoutVersion = computed<AuthLayoutVersion>(() => {
    return getStringDefaultValue({
      key: 'auth_layout_version',
      defaultValue: 'v2',
    }) as AuthLayoutVersion;
  });

  const registerTypeVersion = computed<RegisterTypeVersion>(() => {
    return getStringDefaultValue({
      key: 'register_type_version',
      defaultValue: 'single_step',
    }) as RegisterTypeVersion;
  });

  // Compare values

  const isAuthV2FlowEnabled = computed<boolean>(() => {
    return authLayoutVersion.value === 'v2';
  });

  const authFlowModalWithIframe = computed<boolean>(() => {
    return authFlowVersion.value === 'with_modal_iframe';
  });

  const authFlowWithModal = computed<boolean>(() => {
    return (
      authFlowModalWithIframe.value || authFlowVersion.value === 'with_modal'
    );
  });

  const isMultiStepRegisterEnabled = computed<boolean>(() => {
    return false;
    // return registerTypeVersion.value === 'multi_step';
  });

  const isSingleStepRegisterEnabled = computed<boolean>(() => {
    return (
      registerTypeVersion.value === 'single_step' ||
      registerTypeVersion.value === 'multi_step'
    );
  });

  const isCompleteRegulatoryRegisterEnabled = computed<boolean>(() => {
    return registerTypeVersion.value === 'complete_regulatory';
  });

  const isRegulatorySimplifiedRegisterEnabled = computed<boolean>(() => {
    return registerTypeVersion.value === 'regulatory_simplified';
  });

  // Boolean values

  const isRequestAddressOnRegister = computed<boolean>(() => {
    return getBooleanDefaultValue({
      key: 'register_request_address',
      defaultValue: true,
    });
  });

  const isRegisterRequestDocument = computed<boolean>(() => {
    return getBooleanDefaultValue({
      key: 'register_request_document',
      defaultValue: true,
    });
  });

  const isRegisterRequestBirthDate = computed<boolean>(() => {
    if (isRegisterRequestDocument.value) {
      return false;
    }

    return getBooleanDefaultValue({
      key: 'register_request_birthdate',
      defaultValue: true,
    });
  });

  const isRegisterRequestPhone = computed<boolean>(() => {
    return getBooleanDefaultValue({
      key: 'register_request_phone',
      defaultValue: true,
    });
  });

  const isMultiStepPreRegisterChoiceEnable = computed<boolean>(() => {
    return getBooleanDefaultValue({
      key: 'register_choice_type_active',
      defaultValue: true,
    });
  });

  const isSingleStepirstOnlyCpfEnabled = computed<boolean>(() => {
    if (
      !isSingleStepRegisterEnabled.value ||
      !isRegisterRequestDocument.value
    ) {
      return false;
    }

    return getBooleanDefaultValue({
      key: 'single_step_first_only_cpf',
      defaultValue: false,
    });
  });

  const isTwoFactorAuthEnabled = computed<boolean>(() => {
    return getBooleanDefaultValue({
      key: 'two_factor_auth_active',
      defaultValue: false,
    });
  });

  // Texts values

  const multiStepPreRegisterTitle = computed<string>(() => {
    return getStringDefaultValue({
      key: 'register_choice_type_title',
      defaultValue: '',
    }) as string;
  });

  const multiStepPreRegisterSubtitle = computed<string>(() => {
    return getStringDefaultValue({
      key: 'register_choice_type_subtitle',
      defaultValue: '',
    }) as string;
  });

  const basicRegisterTitle = computed<string>(() => {
    return getStringDefaultValue({
      key: 'register_basic_title',
      defaultValue: '',
    }) as string;
  });

  // Others

  const isInsideUserEntryPage = computed<boolean>(() => {
    return (
      currentRouteName.value === 'register' ||
      currentRouteName.value === 'login' ||
      currentRouteName.value === 'forgot-password' ||
      currentRouteName.value === 'login-two-factor'
    );
  });

  // Functions

  // options.compliance controla os dizeres de compliance no modal (ENG-404):
  // default true (mostra advertencia + +18 + identificacao). Só o botao do
  // header chama com compliance:false para abrir o login/register "limpo".
  function openLogin(options?: { compliance?: boolean }) {
    setAuthComplianceMode(options?.compliance ?? true);

    if (window.self !== window.top) {
      window.parent.postMessage('iframeLoading');
    }

    if (authFlowWithModal.value && !isInsideUserEntryPage.value) {
      openModal('login');
    } else {
      navigateTo(localeLink('/login'));
    }
  }

  function openRecoveryPasswordOptions() {
    if (window.self !== window.top) {
      window.parent.postMessage('iframeLoading');
    }

    if (authFlowWithModal.value && !isInsideUserEntryPage.value) {
      openModal('recovery-password-options');
    }
  }

  function openResetPassword() {
    if (window.self !== window.top) {
      window.parent.postMessage('iframeLoading');
    }

    if (authFlowWithModal.value && !isInsideUserEntryPage.value) {
      openModal('reset-password');
    }
  }

  function openRegister(options?: { compliance?: boolean }) {
    // Cadastro pausado (ENG-899): em vez de abrir o fluxo de registro, abre o
    // popup de aviso na variante "register". Único choke point de todos os
    // pontos que disparam cadastro (botões, ?register=true, etc.).
    if (isMpMaintenanceModeEnabled.value) {
      const { open } = useModalStore();

      open(AnnouncementModal, { variant: 'register' });

      return;
    }

    // Seleciona a aba de registro e delega para o fluxo de login (modal/rota)
    try {
      setAuthInitialTab?.('register');
    } catch {}

    openLogin(options);
  }

  function openForgotPassword() {
    if (window.self !== window.top) {
      window.parent.postMessage('iframeLoading');
    }

    if (authFlowWithModal.value && !isInsideUserEntryPage.value) {
      openModal('forgot-password');
    } else {
      navigateTo(localeLink('/forgot-password'));
    }
  }

  function openTwoFactor(options: TwoFactorOptions = {}) {
    const { loginData, type } = options;

    if (!type) {
      openToaster({
        message: t('auth.login.error.server'),
        type: 'error',
      });
      closeModal();
    }

    userLoginData.value = loginData;
    twoFactorType.value = type;

    if (window.self !== window.top) {
      window.parent.postMessage('iframeLoading');
    }

    if (authFlowWithModal.value && !isInsideUserEntryPage.value) {
      openModal('two-factor-auth');
    } else {
      navigateTo(localeLink('/login/two-factor'));
    }
  }

  function closeAuth() {
    const smarticoMissionsStore = useSmarticoMissions();

    smarticoMissionsStore.loadMissions();

    if (authFlowWithModal.value) {
      closeModal();
      window.parent.postMessage('closeModal');
    } else {
      navigateTo(localeLink('/'));
    }
  }

  return {
    authFlowModalWithIframe,
    authFlowWithModal,
    isAuthV2FlowEnabled,
    isMultiStepRegisterEnabled,
    isMultiStepPreRegisterChoiceEnable,
    multiStepPreRegisterTitle,
    multiStepPreRegisterSubtitle,
    isSingleStepRegisterEnabled,
    isSingleStepirstOnlyCpfEnabled,
    isCompleteRegulatoryRegisterEnabled,
    isRegulatorySimplifiedRegisterEnabled,
    isRequestAddressOnRegister,
    isRegisterRequestDocument,
    isRegisterRequestPhone,
    isRegisterRequestBirthDate,
    basicRegisterTitle,
    isTwoFactorAuthEnabled,
    userLoginData,
    twoFactorType,
    openLogin,
    openRegister,
    openRecoveryPasswordOptions,
    openResetPassword,
    openForgotPassword,
    openTwoFactor,
    closeAuth,
  };
};
